/**
 * Executive Snapshot — Data Validation Utility
 *
 * Validates KPI calculations and flags missing dependencies
 * before display in cards, highlights, and PDF sections.
 */

// ─── Severity levels ──────────────────────────────────────────────────────────
export const SEVERITY = {
  ERROR: 'error',     // Calculation is wrong / dependency missing
  WARNING: 'warning', // Data is present but suspicious / incomplete
  INFO: 'info',       // Informational note (e.g. fallback used)
  OK: 'ok',           // All checks passed
};

// ─── Single validation issue ──────────────────────────────────────────────────
const issue = (severity, field, message) => ({ severity, field, message });

// ─── Helpers ──────────────────────────────────────────────────────────────────
const isNum = (v) => typeof v === 'number' && isFinite(v);
const isPositive = (v) => isNum(v) && v > 0;
const isNonNeg = (v) => isNum(v) && v >= 0;

// ─── Validate Monthly KPI Data ────────────────────────────────────────────────
export const validateMonthlyKPIs = (data) => {
  if (!data) {
    return {
      valid: false,
      severity: SEVERITY?.ERROR,
      issues: [issue(SEVERITY?.ERROR, 'data', 'Monthly snapshot data is unavailable — database query may have failed.')],
    };
  }

  const issues = [];

  // Revenue checks
  if (!isNonNeg(data?.totalRevenue)) {
    issues?.push(issue(SEVERITY?.ERROR, 'totalRevenue', 'Revenue value is missing or invalid.'));
  } else if (data?.totalRevenue === 0 && data?.totalProjects > 0) {
    issues?.push(issue(SEVERITY?.WARNING, 'totalRevenue', `Revenue is OMR 0 but ${data?.totalProjects} project(s) exist — sell_price may be missing on records.`));
  }

  // Profit checks
  if (!isNonNeg(data?.totalProfit) && !isNonNeg(data?.projectProfit)) {
    issues?.push(issue(SEVERITY?.ERROR, 'totalProfit', 'Profit value is missing or invalid.'));
  }

  // Profit vs Revenue consistency
  if (isPositive(data?.totalRevenue) && isNum(data?.totalProfit)) {
    if (data?.totalProfit > data?.totalRevenue) {
      issues?.push(issue(SEVERITY?.ERROR, 'totalProfit', `Profit (${data?.totalProfit?.toFixed(0)}) exceeds Revenue (${data?.totalRevenue?.toFixed(0)}) — check sell_price / total_cost values.`));
    }
  }

  // Profit margin consistency
  if (isPositive(data?.totalRevenue) && isNum(data?.profitMargin)) {
    const expectedMargin = (data?.totalProfit / data?.totalRevenue) * 100;
    if (Math.abs(expectedMargin - data?.profitMargin) > 1) {
      issues?.push(issue(SEVERITY?.WARNING, 'profitMargin', `Margin (${data?.profitMargin?.toFixed(1)}%) does not match Revenue/Profit ratio (${expectedMargin?.toFixed(1)}%).`));
    }
  }

  // Deal / project count checks
  if (!isNonNeg(data?.closedDeals)) {
    issues?.push(issue(SEVERITY?.ERROR, 'closedDeals', 'Closed deals count is invalid.'));
  }
  if (!isNonNeg(data?.openQuotations)) {
    issues?.push(issue(SEVERITY?.ERROR, 'openQuotations', 'Open quotations count is invalid.'));
  }
  if (!isNonNeg(data?.totalProjects)) {
    issues?.push(issue(SEVERITY?.ERROR, 'totalProjects', 'Total projects count is invalid.'));
  }

  // Sub-counts must not exceed total
  const subTotal = (data?.closedDeals ?? 0) + (data?.openQuotations ?? 0) + (data?.activeProjects ?? 0);
  if (isNonNeg(data?.totalProjects) && subTotal > data?.totalProjects) {
    issues?.push(issue(SEVERITY?.WARNING, 'projectCounts', `Sub-category counts (${subTotal}) exceed total projects (${data?.totalProjects}) — possible status overlap.`));
  }

  // Outstanding payments
  if (!isNonNeg(data?.outstandingPayments)) {
    issues?.push(issue(SEVERITY?.WARNING, 'outstandingPayments', 'Outstanding payments value is missing.'));
  }

  // Pipeline value
  if (!isNonNeg(data?.pipelineValue)) {
    issues?.push(issue(SEVERITY?.WARNING, 'pipelineValue', 'Pipeline value is missing.'));
  }

  // Highlights dependencies
  if (data?.totalProjects > 0 && !data?.largestDeal) {
    issues?.push(issue(SEVERITY?.INFO, 'largestDeal', 'Largest deal not identified — project_name or sell_price may be missing.'));
  }
  if (data?.totalProjects > 0 && !data?.topCustomer) {
    issues?.push(issue(SEVERITY?.INFO, 'topCustomer', 'Top customer not identified — client field may be empty on project records.'));
  }
  if (data?.totalProjects > 0 && !data?.topDepartment) {
    issues?.push(issue(SEVERITY?.INFO, 'topDepartment', 'Top department not identified — module/project_type field may be empty.'));
  }
  if (data?.totalProjects > 0 && !data?.topSalesPerson) {
    issues?.push(issue(SEVERITY?.INFO, 'topSalesPerson', 'Top sales performer not identified — estimator/bdm field may be empty.'));
  }

  const severity = issues?.some(i => i?.severity === SEVERITY?.ERROR)
    ? SEVERITY?.ERROR
    : issues?.some(i => i?.severity === SEVERITY?.WARNING)
    ? SEVERITY?.WARNING
    : issues?.some(i => i?.severity === SEVERITY?.INFO)
    ? SEVERITY?.INFO
    : SEVERITY?.OK;

  return { valid: severity !== SEVERITY?.ERROR, severity, issues };
};

// ─── Validate Yearly KPI Data ─────────────────────────────────────────────────
export const validateYearlyKPIs = (data) => {
  if (!data) {
    return {
      valid: false,
      severity: SEVERITY?.ERROR,
      issues: [issue(SEVERITY?.ERROR, 'data', 'Yearly snapshot data is unavailable — database query may have failed.')],
    };
  }

  const issues = [];

  // Revenue
  if (!isNonNeg(data?.totalRevenue) && !isNonNeg(data?.projectRevenue)) {
    issues?.push(issue(SEVERITY?.ERROR, 'totalRevenue', 'Yearly revenue value is missing or invalid.'));
  }

  // Profit vs Revenue
  const rev = data?.totalRevenue || data?.projectRevenue || 0;
  const profit = data?.totalProfit || data?.projectProfit || 0;
  if (isPositive(rev) && profit > rev) {
    issues?.push(issue(SEVERITY?.ERROR, 'totalProfit', `Yearly profit (${profit?.toFixed(0)}) exceeds revenue (${rev?.toFixed(0)}) — check cost/margin data.`));
  }

  // Monthly trend completeness
  if (!data?.monthlyTrend || data?.monthlyTrend?.length !== 12) {
    issues?.push(issue(SEVERITY?.WARNING, 'monthlyTrend', `Monthly trend has ${data?.monthlyTrend?.length ?? 0} entries instead of 12 — chart may be incomplete.`));
  }

  // Best month dependency
  if (isPositive(rev) && !data?.bestMonth) {
    issues?.push(issue(SEVERITY?.WARNING, 'bestMonth', 'Best performing month could not be determined.'));
  }

  // Top 5 customers
  if (data?.totalProjects > 0 && (!data?.top5Customers || data?.top5Customers?.length === 0)) {
    issues?.push(issue(SEVERITY?.INFO, 'top5Customers', 'Top customers list is empty — client field may be missing on project records.'));
  }

  // Top 5 deals
  if (data?.totalProjects > 0 && (!data?.top5Deals || data?.top5Deals?.length === 0)) {
    issues?.push(issue(SEVERITY?.INFO, 'top5Deals', 'Top deals list is empty — project_name or sell_price may be missing.'));
  }

  // Avg monthly revenue sanity
  if (isPositive(rev) && isNum(data?.avgMonthlyRevenue)) {
    const expected = rev / 12;
    if (Math.abs(expected - data?.avgMonthlyRevenue) > 1) {
      issues?.push(issue(SEVERITY?.WARNING, 'avgMonthlyRevenue', `Avg monthly revenue (${data?.avgMonthlyRevenue?.toFixed(0)}) does not match total/12 (${expected?.toFixed(0)}).`));
    }
  }

  const severity = issues?.some(i => i?.severity === SEVERITY?.ERROR)
    ? SEVERITY?.ERROR
    : issues?.some(i => i?.severity === SEVERITY?.WARNING)
    ? SEVERITY?.WARNING
    : issues?.some(i => i?.severity === SEVERITY?.INFO)
    ? SEVERITY?.INFO
    : SEVERITY?.OK;

  return { valid: severity !== SEVERITY?.ERROR, severity, issues };
};

// ─── Validate a single KPI field ──────────────────────────────────────────────
export const validateKPIField = (fieldName, value, data) => {
  const fieldChecks = {
    totalRevenue: () => {
      if (!isNonNeg(value)) return issue(SEVERITY?.ERROR, fieldName, 'Revenue is missing or invalid.');
      if (value === 0 && (data?.totalProjects ?? 0) > 0)
        return issue(SEVERITY?.WARNING, fieldName, 'Revenue is OMR 0 despite existing projects — sell_price may be missing.');
      return null;
    },
    totalProfit: () => {
      if (!isNonNeg(value)) return issue(SEVERITY?.ERROR, fieldName, 'Profit is missing or invalid.');
      const rev = data?.totalRevenue || data?.projectRevenue || 0;
      if (isPositive(rev) && value > rev)
        return issue(SEVERITY?.ERROR, fieldName, 'Profit exceeds revenue — cost data may be incorrect.');
      return null;
    },
    profitMargin: () => {
      if (!isNonNeg(value)) return issue(SEVERITY?.WARNING, fieldName, 'Profit margin is missing.');
      if (value > 100) return issue(SEVERITY?.ERROR, fieldName, `Margin ${value?.toFixed(1)}% exceeds 100% — calculation error.`);
      return null;
    },
    pipelineValue: () => {
      if (!isNonNeg(value)) return issue(SEVERITY?.WARNING, fieldName, 'Pipeline value is missing.');
      return null;
    },
    outstandingPayments: () => {
      if (!isNonNeg(value)) return issue(SEVERITY?.WARNING, fieldName, 'Outstanding payments value is missing.');
      return null;
    },
  };

  const check = fieldChecks?.[fieldName];
  return check ? check() : null;
};

// ─── Validate PDF section data ────────────────────────────────────────────────
export const validatePDFData = (monthlyData, yearlyData) => {
  const issues = [];

  if (!monthlyData) {
    issues?.push(issue(SEVERITY?.ERROR, 'monthly', 'Monthly data is missing — PDF monthly section will be empty.'));
  } else {
    const mv = validateMonthlyKPIs(monthlyData);
    issues?.push(...(mv?.issues || []));
  }

  if (!yearlyData) {
    issues?.push(issue(SEVERITY?.ERROR, 'yearly', 'Yearly data is missing — PDF yearly section will be empty.'));
  } else {
    const yv = validateYearlyKPIs(yearlyData);
    issues?.push(...(yv?.issues || []));
  }

  const hasErrors = issues?.some(i => i?.severity === SEVERITY?.ERROR);
  const hasWarnings = issues?.some(i => i?.severity === SEVERITY?.WARNING);

  return {
    canExport: !hasErrors,
    severity: hasErrors ? SEVERITY?.ERROR : hasWarnings ? SEVERITY?.WARNING : SEVERITY?.OK,
    issues,
  };
};

// ─── Format validation summary for display ────────────────────────────────────
export const formatValidationSummary = (result) => {
  if (!result?.issues?.length) return null;
  const errors = result?.issues?.filter(i => i?.severity === SEVERITY?.ERROR);
  const warnings = result?.issues?.filter(i => i?.severity === SEVERITY?.WARNING);
  const infos = result?.issues?.filter(i => i?.severity === SEVERITY?.INFO);
  return { errors, warnings, infos, total: result?.issues?.length };
};
